import React, { useMemo } from "react";
import Stockchart from "../components/Stockchart";
import { useDataWithRoute } from "../utils/hooks";
import { VsiBubble } from "../styled/App.styled";
import { formatDualAxisStockChartConfig, formatStockChartConfig } from "../utils/Helpers";
import { allSpaceTypes } from "../utils/utils";

import {
  standardCapacityColors,
  allPerformance, genericColors
} from "../utils/chartUtils";

import { chartTypes } from "../utils/utils";
import { useVsiAppContext } from "../stores/VsiApp";
import { Spinner } from "reactstrap";

const chartMetrics = [
  { value: "history", label: "History", route: "_proc", yAxis: "GiB" },
  { value: "by_datacenter", label: "By Data Center", route: "_by_datacenter_proc", yAxis: "Ratio" },
  { value: "by_device_type", label: "By Device Type", route: "_by_device_type_proc", yAxis: "# Red Light Alerts" },
  { value: "by_classification", label: "By Classification", route: "_by_classification_proc", yAxis: "GiB" },
  { value: "by_application", label: "By Application", route: "_by_application_proc", yAxis: "GiB" },
  { value: "all_data_centers", label: "All Data Centers", route: "_by_all_datacenters_proc", yAxis: "GiB" },
  { value: "all_device_types", label: "All Device Types", route: "_by_all_device_types_proc", yAxis: "GiB" },
  { value: "all_classifications", label: "All Classifications", route: "_by_all_classification_proc", yAxis: "GiB" },
  { value: "all_applications", label: "All Applications", route: "_by_all_application_proc", yAxis: "GiB" }
];

const ratioChartMetrics = [
  { value: "all_device_types", label: "All Device Types", yAxis: "GiB" },
  { value: "all_classifications", label: "All Classifications", yAxis: "GiB" },
  { value: "all_applications", label: "All Applications", yAxis: "GiB" },
  { value: "by_device_type", label: "By Device Type", yAxis: "GiB" },
  { value: "by_classification", label: "By Classification", yAxis: "GiB" },
  { value: "by_application", label: "By Application", yAxis: "GiB" }
];

export default function EnterpriseTrends({ params }) {
  const { client: { clientId }, getPeriodId } = useVsiAppContext();
  const periodId = getPeriodId("STORAGE");

  const typeObj = useMemo(() => params?.find?.(({ param }) => param === "type") ?? {}, [params]);
  const type = typeObj?.value ?? "capacity";

  const metricObj = useMemo(() => params?.find?.(({ param }) => param === "metric") ?? {}, [params]);
  const metric = metricObj?.value || "history";

  const addtlOptionObj = useMemo(() => params?.find?.(({ param }) => param !== "metric" && param !== "type") ?? {}, [params]);
  const addtlValue = addtlOptionObj?.value || "";

  const chartType = useMemo(() => chartTypes.find(({ value }) => type === value) || chartTypes[0], [type]);
  const { value: chartTypeValue } = chartType;

  const chosenMetrics = chartTypeValue === "data_reduction" ? ratioChartMetrics : chartMetrics;

  const chartMetric = useMemo(() => chosenMetrics.find(({ value }) => metric === value) || chosenMetrics[0], [metric, chosenMetrics]);
  const { route: metricRoute, value: metricValue } = chartMetric;

  const route = useMemo(() => {
    let route = metricRoute;
    // matching what's in the report page
    if (type && type === "data_reduction") {
      if (metric === "all_device_types") {
        route = "_by_all_device_types";
      } else if (metric === "all_classifications") {
        route = "_by_all_classification";
      } else if (metric === "all_applications") {
        route = "_by_all_application";
      } else {
        route = `_${metric}`;
      }
      route += "_proc";
    }

    let routeToSearch = `/api/v6/report/enterprise_trends${encodeURI(route)}?client_id=${clientId}&period_id=${periodId}`;

    if (metric === "by_datacenter" && addtlValue) {
      routeToSearch += `&data_center=${addtlValue}`;
    }
    if (metric === "by_device_type" && addtlValue) {
      routeToSearch += `&device_type=${addtlValue}`;
    }
    if (metric === "by_classification" && addtlValue) {
      routeToSearch += `&classification=${addtlValue}`;
    }
    if (metric === "by_application" && addtlValue) {
      routeToSearch += `&application=${addtlValue}`;
    }
    return routeToSearch;
  }, [metricRoute, clientId, periodId, addtlValue, metric, type]);

  const [{ recordsets }, { isLoading }] = useDataWithRoute(route);

  const { chartConfig, formattedChartData } = useMemo(() => {
    const sortedData = recordsets?.[0]?.sort(
      (a, b) => new Date(a.collection_date) - new Date(b.collection_date)
    );

    let chartConfig = { colors: genericColors };

    switch (metricValue) {
      case "history":
        if (chartTypeValue === "capacity") {
          chartConfig = {
            ...chartConfig,
            ...formatStockChartConfig(sortedData || [], [
              { name: "Free", field: "free" },
              { name: "Used", field: "used" },
              { name: "Allocated", field: "allocated" },
              { name: "Capacity", field: "capacity" },
              { name: "Orphaned", field: "orphaned" },
              { name: "Snap", field: "snap" }
            ]),
            colors: standardCapacityColors,
            yAxis: { title: { text: "GiB" } }
          };
        } else if (chartTypeValue === "phys_capacity") {
          chartConfig = {
            ...chartConfig,
            ...formatStockChartConfig(sortedData || [], [
              { name: "Phys. Free", field: "physical_free" },
              { name: "Phys. Used", field: "physical_used" },
              { name: "Allocated", field: "allocated" },
              { name: "Phys. Capacity", field: "physical_capacity" },
              { name: "Orphaned", field: "orphaned" },
              { name: "Snap", field: "snap" }
            ]),
            colors: standardCapacityColors,
            yAxis: { title: { text: "GiB" } }
          };
        } else {
          chartConfig = {
            ...chartConfig,
            ...formatDualAxisStockChartConfig(sortedData || [], [
              { name: "IOPS", field: "iops", yAxis: 0 },
              { name: "Latency", field: "latency", yAxis: 1 }
            ]),
            colors: allPerformance,
            yAxis: [
              { title: { text: "IOPS" }, opposite: false },
              { title: { text: "Latency (ms)" }, opposite: true }
            ]
          };
        }
        break;

      case "by_datacenter":
      case "by_device_type":
      case "by_classification":
      case "by_application":
        if (chartTypeValue === "capacity") {
          chartConfig = {
            ...chartConfig,
            ...formatStockChartConfig(sortedData || [], [
              { name: "Free", field: "free" },
              { name: "Used", field: "used" },
              { name: "Allocated", field: "allocated" },
              { name: "Capacity", field: "capacity" },
              { name: "Orphaned", field: "orphaned" },
              { name: "Snap", field: "snap" }
            ]),
            colors: standardCapacityColors,
            yAxis: { title: { text: "GiB" } }
          };
        } else if (chartTypeValue === "phys_capacity") {
          chartConfig = {
            ...chartConfig,
            ...formatStockChartConfig(sortedData || [], [
              { name: "Phys. Free", field: "physical_free" },
              { name: "Phys. Used", field: "physical_used" },
              { name: "Allocated", field: "allocated" },
              { name: "Phys. Capacity", field: "physical_capacity" },
              { name: "Orphaned", field: "orphaned" },
              { name: "Snap", field: "snap" }
            ]),
            colors: standardCapacityColors,
            yAxis: { title: { text: "GiB" } }
          };
        } else if (chartTypeValue === "data_reduction") {
          chartConfig = {
            ...chartConfig,
            ...formatStockChartConfig(sortedData || [], [
              { name: "Data Reduction Ratio", field: "DRR" }
            ]),
            colors: standardCapacityColors,
            yAxis: { title: { text: "Data Reduction Ratio" } }
          };
        } else {
          chartConfig = {
            ...chartConfig,
            ...formatDualAxisStockChartConfig(sortedData || [], [
              { name: "IOPS", field: "iops", yAxis: 0 },
              { name: "Latency", field: "latency", yAxis: 1 }
            ]),
            colors: allPerformance,
            yAxis: [
              { title: { text: "IOPS" }, opposite: false },
              { title: { text: "Latency (ms)" }, opposite: true }
            ]
          };
        }
        break;

      case "all_data_centers": {
        let findDcs = {};
        const foundDcsData = sortedData?.map(datum => {
          if (!findDcs[datum.data_center]) findDcs[datum.data_center] = true;
          const returnDatum = {};
          for (let key in datum) {
            if (datum.hasOwnProperty(key)) {
              returnDatum[!key.charAt(0).match(/[A-Z\u00C0-\u00DC]+/)
                ? key
                : key.toLowerCase()
                ]
                = datum[key];
            }
          }
          return returnDatum;
        });
        const fields = Object.keys(findDcs).map(dc => ({
          name: dc,
          field: params?.[2]?.value || allSpaceTypes[0].value
        }));
        chartConfig = {
          ...chartConfig,
          ...formatStockChartConfig(foundDcsData || [], fields, "data_center"),
          yAxis: { title: { text: chartTypeValue === "data_reduction" ? "Data Reduction Ratio" : "GiB" } }
        };
        break;
      }

      case "all_device_types": {
        let findDts = {};
        const foundDtsData = sortedData?.map(datum => {
          if (!findDts[datum.device_type_code]) findDts[datum.device_type_code] = true;
          const returnDatum = {};
          for (let key in datum) {
            if (datum.hasOwnProperty(key)) {
              returnDatum[!key.charAt(0).match(/[A-Z\u00C0-\u00DC]+/)
                ? key
                : key.toLowerCase()
                ]
                = datum[key];
            }
          }
          return returnDatum;
        });
        const fieldVal = chartTypeValue === "data_reduction" ? "drr" : params?.[2]?.value || allSpaceTypes[0].value;
        const fields = Object.keys(findDts).map(dt => ({
          name: dt,
          field: fieldVal
        }));
        chartConfig = {
          ...chartConfig,
          ...formatStockChartConfig(foundDtsData || [], fields, "device_type_code"),
          yAxis: { title: { text: fieldVal === "drr" ? "Data Reduction Ratio" : "GiB" } }
        };
        break;
      }

      case "all_classifications": {
        let findCs = {};
        const foundCsData = sortedData?.map(datum => {
          if (!findCs[datum.classification]) findCs[datum.classification] = true;
          const returnDatum = {};
          for (let key in datum) {
            if (datum.hasOwnProperty(key)) {
              returnDatum[!key.charAt(0).match(/[A-Z\u00C0-\u00DC]+/)
                ? key
                : key.toLowerCase()
                ]
                = datum[key];
            }
          }
          return returnDatum;
        });
        const fieldVal = chartTypeValue === "data_reduction" ? "drr" : params?.[2]?.value || allSpaceTypes[0].value;
        const fields = Object.keys(findCs).map(cl => ({
          name: cl,
          field: fieldVal
        }));
        chartConfig = {
          ...chartConfig,
          ...formatStockChartConfig(foundCsData || [], fields, "classification"),
          yAxis: { title: { text: fieldVal === "drr" ? "Data Reduction Ratio" : "GiB" } }
        };
        break;
      }

      case "all_applications": {
        let findAp = {};
        const foundApData = sortedData?.map(datum => {
          if (!findAp[datum.application]) findAp[datum.application] = true;
          const returnDatum = {};
          for (let key in datum) {
            if (datum.hasOwnProperty(key)) {
              returnDatum[!key.charAt(0).match(/[A-Z\u00C0-\u00DC]+/)
                ? key
                : key.toLowerCase()
                ]
                = datum[key];
            }
          }
          return returnDatum;
        });
        const fieldVal = chartTypeValue === "data_reduction" ? "drr" : params?.[2]?.value || allSpaceTypes[0].value;
        const fields = Object.keys(findAp).map(app => ({
          name: app,
          field: fieldVal
        }));
        chartConfig = {
          ...chartConfig,
          ...formatStockChartConfig(foundApData || [], fields, "application"),
          yAxis: { title: { text: fieldVal === "drr" ? "Data Reduction Ratio" : "GiB" } }
        };
        break;
      }

      default:
        break;
    }

    return {
      chartConfig,
      formattedChartData: chartConfig.series
    };
  }, [recordsets, metricValue, chartTypeValue, params]);

  return isLoading ? (
    <Spinner color={"success"} />
  ) : (
    <VsiBubble>
      <Stockchart series={formattedChartData}
                  config={chartConfig}
                  height={400}
                  navDisabled={true} />
    </VsiBubble>
  );
}
